"use client";

import React from "react";
import { PORTAL_THEME } from "../../lib/portal_theme";
import { SUBSCRIPTION_PRICE, SUBSCRIPTION_TITLE } from "../../lib/subscription_fields.js";
import Spinner from "../components/Spinner";

const T = PORTAL_THEME;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : null);

const pill = (primary) => ({
  padding: "12px 22px", borderRadius: 0, textTransform: "uppercase", letterSpacing: "1px", fontWeight: 700, fontSize: 14, fontFamily: T.fontFamily, cursor: "pointer",
  ...(primary
    ? { background: T.chromeGradient, border: T.chromeBorder, boxShadow: T.chromeButtonShadow, color: T.chromeButtonText }
    : { background: "transparent", border: `1px solid ${T.cardBorder}`, color: T.text }),
});

// Manage the Unlimited Instant Graphics subscription (lib/subscription.js):
// status, next charge, Cancel. Linked from the Subscription card on /service.
export default function SubscriptionPage() {
  const [sub, setSub] = React.useState(null);
  const [busy, setBusy] = React.useState("");
  const [confirming, setConfirming] = React.useState(false);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    const d = await fetch("/api/subscription?fresh=1", { cache: "no-store", credentials: "include" }).then((r) => r.json()).catch(() => null);
    if (d?.ok && !d.signedIn) {
      window.location.href = `/signin?noGuest=1&returnTo=${encodeURIComponent("/subscription")}`;
      return;
    }
    setSub(d || { status: "none" });
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    setBusy(action); setError("");
    try {
      const res = await fetch("/api/subscription", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d?.ok) throw new Error(d?.error === "subscription_not_configured"
        ? "Subscriptions aren't available yet — please check back soon." : (d?.error || `Failed (${res.status})`));
      if (action === "subscribe") { window.location.href = d.checkoutUrl; return; }
      setSub(d); setConfirming(false);
    } catch (e) { setError(String(e?.message || e)); }
    setBusy("");
  };

  return (
    <div style={{ minHeight: "100vh", fontFamily: T.fontFamily, color: T.text }}>
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "48px 20px" }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, margin: "0 0 6px", letterSpacing: "-0.5px" }}>Your Subscription</h1>
        <p style={{ color: T.textMuted, fontSize: 14, margin: "0 0 28px" }}>
          {SUBSCRIPTION_TITLE} — {SUBSCRIPTION_PRICE} a month for every announcement video, graphic and custom print image.
        </p>

        <div style={{ border: T.cardEdge, boxShadow: "none", borderRadius: 0, background: T.cardBackground, padding: "22px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          {!sub ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: T.textMuted }}><Spinner size={16} /> Checking your subscription…</div>
          ) : sub.status === "active" ? (
            <>
              <div style={{ fontSize: 16, fontWeight: 700 }}>✓ You are subscribed</div>
              <div style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.6 }}>
                Instant Graphics are included for prints and announcements.
                {sub.nextBillingDate && <> Your next {SUBSCRIPTION_PRICE} charge is on <strong style={{ color: T.text }}>{fmtDate(sub.nextBillingDate)}</strong>.</>}
              </div>
              {!confirming ? (
                <div><button type="button" style={pill(false)} onClick={() => setConfirming(true)}>Cancel Subscription</button></div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                    Cancel your subscription? You won&apos;t be charged again{sub.nextBillingDate ? <>, and Instant Graphics stay included until {fmtDate(sub.nextBillingDate)}</> : null}.
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button type="button" style={pill(false)} disabled={!!busy} onClick={() => act("cancel")}>
                      {busy === "cancel" ? "Cancelling…" : "Yes, cancel"}
                    </button>
                    <button type="button" style={pill(true)} disabled={!!busy} onClick={() => setConfirming(false)}>Keep my subscription</button>
                  </div>
                </div>
              )}
            </>
          ) : sub.status === "cancelled" ? (
            <>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Subscription cancelled</div>
              <div style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.6 }}>
                You won&apos;t be charged again. Instant Graphics stay included until <strong style={{ color: T.text }}>{fmtDate(sub.endsAt)}</strong>.
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 16, fontWeight: 700 }}>You don&apos;t have a subscription</div>
              <div style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.6 }}>
                Subscribe for {SUBSCRIPTION_PRICE} a month and every Instant Graphic is included — no checkout per design. Cancel any time.
                {sub.error && " (We couldn't reach Shopify just now — if you subscribed recently, refresh in a minute.)"}
              </div>
              <div><button type="button" style={pill(true)} disabled={!!busy} onClick={() => act("subscribe")}>
                {busy === "subscribe" ? "Redirecting to checkout…" : `Subscribe — ${SUBSCRIPTION_PRICE}/mo →`}
              </button></div>
            </>
          )}
          {error && <p style={{ color: T.error, fontSize: 13, margin: 0 }}>{error}</p>}
        </div>

        <p style={{ margin: "18px 0 0", fontSize: 13 }}>
          <a href="/service?flow=announcement" style={{ color: T.accentOrange, fontWeight: 700, textDecoration: "none" }}>Start a new design →</a>
        </p>
      </div>
    </div>
  );
}
